import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import ArrowButton from "@/design-system/ArrowButton/ArrowButton";
import { cn } from "@/lib/utils";
import React, { useCallback, useEffect, useRef, useState } from "react";

const SCROLL_THRESHOLD = 2;

interface CarouselProps {
  header?: React.ReactNode;
  items: React.ReactNode[];
  description?: string;
  gap?: string;
  showArrows?: boolean;
  className?: string;
  icon?: React.ReactNode;
  marginLeft?: string;
  shadowDirection?: "x" | "y" | "both";
  enableShadows?: boolean;
  showScrollBar?: boolean;
  scrollBarColor?: string;
}

export const Carousel = ({
  header,
  items,
  description,
  gap = "gap-2",
  showArrows = false,
  icon,
  marginLeft = "ml-2",
  shadowDirection = "x",
  enableShadows = false,
  showScrollBar = false,
  scrollBarColor,
}: CarouselProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getViewport = useCallback(
    () =>
      scrollContainerRef.current?.querySelector(
        "[data-radix-scroll-area-viewport]",
      ) as HTMLElement | null,
    [],
  );

  const updateScrollState = useCallback(() => {
    const viewport = getViewport();
    if (!viewport) return;

    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    const maxScrollLeft = scrollWidth - clientWidth;

    setCanScrollLeft(scrollLeft > SCROLL_THRESHOLD);
    setCanScrollRight(scrollLeft < maxScrollLeft - SCROLL_THRESHOLD);
  }, [getViewport]);

  useEffect(() => {
    if (!showArrows) return;

    const setup = () => {
      const viewport = getViewport();
      if (!viewport) return null;

      updateScrollState();

      const handleScroll = () => updateScrollState();
      viewport.addEventListener("scroll", handleScroll, { passive: true });

      const resizeObserver = new ResizeObserver(updateScrollState);
      resizeObserver.observe(viewport);

      return () => {
        viewport.removeEventListener("scroll", handleScroll);
        resizeObserver.disconnect();
      };
    };

    let cleanup = setup();
    if (!cleanup) {
      const timeoutId = window.setTimeout(() => {
        cleanup = setup();
      }, 0);
      return () => {
        window.clearTimeout(timeoutId);
        cleanup?.();
      };
    }
    return cleanup;
  }, [getViewport, updateScrollState, items.length, showArrows]);

  const scroll = useCallback(
    (direction: "left" | "right") => {
      const viewport = getViewport();
      if (!viewport) return;

      viewport.scrollBy({
        left:
          direction === "left" ? -viewport.clientWidth : viewport.clientWidth,
        behavior: "smooth",
      });
    },
    [getViewport],
  );

  return (
    <div className="relative">
      <div className="flex flex-col gap-1 mb-3">
        <div className="flex items-center justify-between">
          <div className={cn("flex items-center gap-2 min-w-0", marginLeft)}>
            {icon && icon}
            {header && (
              <div className="min-w-0 overflow-x-auto whitespace-nowrap">
                {header}
              </div>
            )}
          </div>
          {showArrows && (
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  !canScrollLeft && "pointer-events-none invisible",
                )}
              >
                <ArrowButton
                  direction="left"
                  onClick={() => scroll("left")}
                  disabled={!canScrollLeft}
                />
              </div>
              <div
                className={cn(
                  "mr-2",
                  !canScrollRight && "pointer-events-none invisible",
                )}
              >
                <ArrowButton
                  direction="right"
                  onClick={() => scroll("right")}
                  disabled={!canScrollRight}
                />
              </div>
            </div>
          )}
        </div>
        {description && (
          <p
            className={cn(
              "flex flex-row gap-2 text-sm font-medium text-dark-tertiary",
              marginLeft,
            )}
          >
            {icon && <div className="size-5" />}
            {description}
          </p>
        )}
      </div>
      <ScrollArea
        ref={scrollContainerRef}
        blockClickOnScroll
        shadowDirection={shadowDirection}
        enableShadows={enableShadows}
      >
        <div className={cn("flex flex-row mb-1", marginLeft, gap)}>
          {items.map((item, index) => (
            <div key={index} className="shrink-0">
              {item}
            </div>
          ))}
        </div>
        <ScrollBar
          orientation="horizontal"
          className={cn(!showScrollBar && "hidden")}
          scrollBarColor={scrollBarColor}
        />
        <div className="h-2"></div>
      </ScrollArea>
    </div>
  );
};
